import React, { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import { authManager } from '../utils/auth';

const Base_Url = process.env.REACT_APP_API_BASE_URL;

const SEGMENTS = ['mental_health', 'financial', 'family', 'all'];
const NURTURE_TRACKS = ['cca_loop', 'goaltide', 'mml_content', 'all_inclusive'];

function QuizReportRulesAdd() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(id ? true : false);
    const [saving, setSaving] = useState(false);

    const [formData, setFormData] = useState({
        segment: SEGMENTS[0],
        headline: '',
        gapText: '',
        premiumValueText: '',
        nurtureTrack: NURTURE_TRACKS[0],
        isActive: true,
    });

    const fetchRuleDetails = useCallback(async () => {
        try {
            const res = await authManager.authenticatedFetch(
                `${Base_Url}/admin/quiz/report-rules/${id}`
            );
            const data = await res.json();

            if (data.status === 'success') {
                const rule = data.data;
                setFormData({
                    segment: rule.segment,
                    headline: rule.headline || '',
                    gapText: rule.gapText || '',
                    premiumValueText: rule.premiumValueText || '',
                    nurtureTrack: rule.nurtureTrack || NURTURE_TRACKS[0],
                    isActive: rule.isActive,
                });
            } else {
                toast.error('Failed to fetch report rule details');
            }
        } catch (error) {
            console.error('Error fetching report rule:', error);
            toast.error('Error fetching report rule details');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        if (id) {
            fetchRuleDetails();
        }
    }, [id, fetchRuleDetails]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.segment) {
            toast.error('Segment is required');
            return;
        }

        try {
            setSaving(true);

            const url = id
                ? `${Base_Url}/admin/quiz/report-rules/${id}`
                : `${Base_Url}/admin/quiz/report-rules`;

            const method = id ? 'PUT' : 'POST';

            const res = await authManager.authenticatedFetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData)
            });

            const data = await res.json();

            if (data.status === 'success') {
                toast.success(id ? 'Report rule updated successfully!' : 'Report rule created successfully!');
                setTimeout(() => navigate('/quizReportRules'), 1500);
            } else {
                toast.error(data.message || 'Operation failed');
            }
        } catch (err) {
            console.error('Error saving report rule:', err);
            toast.error('Error saving report rule');
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <ToastContainer autoClose={3000} />
            <div className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2 align-items-center">
                        <div className="col-6 col-sm-6">
                            <h1 className="m-0 txt_lft">{id ? 'Edit' : 'Add'} Report Rule</h1>
                        </div>
                        <div className="col-6 col-sm-6">
                            <ol className="breadcrumb justify-content-end">
                                <li className="breadcrumb-item">
                                    <Link to="/quizReportRules">
                                        <i className="fa-solid fa-arrow-left-long" /> Back
                                    </Link>
                                </li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>

            <div className="content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="frmwdth_cntr">
                            <div className="col-lg-12">
                                <div className="card card-primary card-outline">
                                    {loading ? (
                                        <div className="card-body text-center">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="sr-only">Loading...</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <form className="form-horizontal" onSubmit={handleSubmit}>
                                            <div className="card-body">
                                                <div className="form-group row">
                                                    <label className="col-12 col-md-3 col-form-label">Segment *</label>
                                                    <div className="col-12 col-md-9">
                                                        <select
                                                            className="form-control"
                                                            name="segment"
                                                            value={formData.segment}
                                                            onChange={handleInputChange}
                                                            disabled={!!id}
                                                        >
                                                            {SEGMENTS.map((s) => (
                                                                <option key={s} value={s}>{s}</option>
                                                            ))}
                                                        </select>
                                                        <small className="text-muted">
                                                            Matches the value from the "interest" quiz question. One rule per segment.
                                                        </small>
                                                    </div>
                                                </div>

                                                <div className="form-group row">
                                                    <label className="col-12 col-md-3 col-form-label">Headline</label>
                                                    <div className="col-12 col-md-9">
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            name="headline"
                                                            value={formData.headline}
                                                            onChange={handleInputChange}
                                                            placeholder="e.g., You have free support - but it caps out."
                                                        />
                                                    </div>
                                                </div>

                                                <div className="form-group row">
                                                    <label className="col-12 col-md-3 col-form-label">Gap Text</label>
                                                    <div className="col-12 col-md-9">
                                                        <textarea
                                                            className="form-control"
                                                            name="gapText"
                                                            rows="2"
                                                            value={formData.gapText}
                                                            onChange={handleInputChange}
                                                            placeholder="What the free benefit gives you, and where it caps out"
                                                        />
                                                    </div>
                                                </div>

                                                <div className="form-group row">
                                                    <label className="col-12 col-md-3 col-form-label">Premium Value Text</label>
                                                    <div className="col-12 col-md-9">
                                                        <textarea
                                                            className="form-control"
                                                            name="premiumValueText"
                                                            rows="2"
                                                            value={formData.premiumValueText}
                                                            onChange={handleInputChange}
                                                            placeholder="What Circle1 Premium adds on top"
                                                        />
                                                    </div>
                                                </div>

                                                <div className="form-group row">
                                                    <label className="col-12 col-md-3 col-form-label">Nurture Track</label>
                                                    <div className="col-12 col-md-9">
                                                        <select
                                                            className="form-control"
                                                            name="nurtureTrack"
                                                            value={formData.nurtureTrack}
                                                            onChange={handleInputChange}
                                                        >
                                                            {NURTURE_TRACKS.map((t) => (
                                                                <option key={t} value={t}>{t}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>

                                                <div className="form-group row">
                                                    <label className="col-12 col-md-3 col-form-label">Active</label>
                                                    <div className="col-12 col-md-9">
                                                        <div className="custom-control custom-switch mt-2">
                                                            <input
                                                                type="checkbox"
                                                                className="custom-control-input"
                                                                id="isActive"
                                                                name="isActive"
                                                                checked={formData.isActive}
                                                                onChange={handleInputChange}
                                                            />
                                                            <label className="custom-control-label" htmlFor="isActive">
                                                                {formData.isActive ? 'Live for this segment' : 'Not shown to leads'}
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="card-footer text-center">
                                                <button
                                                    type="submit"
                                                    className="btn btn-blue"
                                                    disabled={saving}
                                                >
                                                    {saving ? 'Saving...' : (id ? 'Update' : 'Create')}
                                                </button>
                                            </div>
                                        </form>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default QuizReportRulesAdd
